import { map, mergeMap, catchError, tap, filter } from 'rxjs/operators'
import { of } from 'rxjs'
import { ofType, combineEpics } from 'redux-observable'

import { logger } from '../../util/log.helpers'
import {
  FILE_UPLOAD_ACTIONS,
  NFTS_PROFILE_ACTIONS
} from '../../constants/actions'
import apiService from '../../services/api'
import { wrapUserAccessToken } from '../../util/auth.helpers'
import { buildFileUrl } from '../../util/brand.helpers'
import { commonParser } from '../../util/apiParser.helpers'
import {
  buildRedeemExclCntData,
  buildUpdateNftData,
  isNftStatusFilter,
  nftFieldsFormat
} from '../../util/nft.helpers'
import {
  buildPaginationFilters,
  compareFileUploadType,
  extractBrandId,
  extractFileUploadType,
  extractNftsStatus,
  extractNftUsersPagination,
  extractTokenInfo,
  formatTokens,
  parseNftUsersResponse
} from '../../util/epics.helpers'
import { FILE_UPLOAD_TYPE } from '../../constants/input'
import { TOKEN_TYPE } from '../../constants/token'
import { NFT_STATUS_OPTIONS } from '../../data/nfts'
import { LOYALTY_EVENT_TYPES } from '../../constants/transactionTypes'

const handleCreateNftEpic = (action$, state$) =>
  action$.pipe(
    ofType(FILE_UPLOAD_ACTIONS.ON_UPLOAD_FILE_SUCCESS),
    filter(() =>
      compareFileUploadType(
        extractFileUploadType(state$),
        FILE_UPLOAD_TYPE.CREATE_NFT
      )
    ),
    mergeMap(({ payload }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .createToken(
            extractBrandId(state$),
            extractCreateNftData(
              state$,
              buildFileUrl(payload),
              extractTokenInfo(state$).onramp
            ),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => {
              return {
                type: NFTS_PROFILE_ACTIONS.ON_CREATE_NFT_SUCCESS,
                payload: commonParser(response.data)
              }
            }),
            catchError((error) =>
              of({
                type: NFTS_PROFILE_ACTIONS.ON_CREATE_NFT_FAILED,
                payload: error
              })
            )
          )
      )
    )
  )

const handleUpdateNftEpic = (action$, state$) =>
  action$.pipe(
    ofType(NFTS_PROFILE_ACTIONS.ON_UPDATE_NFT),
    mergeMap(({ payload: { showNft, tokenId } }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .updateToken(
            extractBrandId(state$),
            tokenId,
            buildUpdateNftData(showNft),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => {
              return {
                type: NFTS_PROFILE_ACTIONS.ON_UPDATE_NFT_SUCCESS,
                payload: commonParser(response.data)
              }
            }),
            catchError((error) =>
              of({
                type: NFTS_PROFILE_ACTIONS.ON_UPDATE_NFT_FAILED,
                payload: error
              })
            )
          )
      )
    )
  )

const handleTransferNftEpic = (action$, state$) =>
  action$.pipe(
    ofType(NFTS_PROFILE_ACTIONS.ON_TRANSFER_NFT),
    mergeMap(({ payload: { authId, tokenNftId } }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .transferNft(
            extractBrandId(state$),
            buildTransferNftData(authId, tokenNftId),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(() => {
              return {
                type: NFTS_PROFILE_ACTIONS.ON_TRANSFER_NFT_SUCCESS,
                payload: { tokenId: tokenNftId, pageNumber: 0 }
              }
            }),
            catchError((error) =>
              of({
                type: NFTS_PROFILE_ACTIONS.ON_TRANSFER_NFT_FAILED,
                payload: error
              })
            )
          )
      )
    )
  )

const handleGetNftsEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      NFTS_PROFILE_ACTIONS.ON_GET_NFTS,
      NFTS_PROFILE_ACTIONS.ON_CREATE_NFT_SUCCESS
    ),
    map(() => ({
      brandId: extractBrandId(state$),
      status: extractNftsStatus(state$)
    })),
    filter(({ brandId }) => brandId),
    mergeMap(({ brandId, status }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .getTokens(brandId, accessToken, {
            types: [TOKEN_TYPE.ERC_721],
            ...(status !== NFT_STATUS_OPTIONS.all && { status })
          })
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => {
              return {
                type: NFTS_PROFILE_ACTIONS.ON_GET_NFTS_SUCCESS,
                payload: formatTokens(response.data)
              }
            }),
            catchError(() =>
              of({
                type: NFTS_PROFILE_ACTIONS.ON_GET_NFTS_FAILED
              })
            )
          )
      )
    )
  )

const handleOnNftsFilterChangeEpic = (action$) =>
  action$.pipe(
    ofType(NFTS_PROFILE_ACTIONS.CHANGE_STATE),
    filter(({ payload }) => isNftStatusFilter(payload)),
    map(() => {
      return {
        type: NFTS_PROFILE_ACTIONS.ON_GET_NFTS
      }
    })
  )

const handleGetNftUsers = (action$, state$) =>
  action$.pipe(
    ofType(
      NFTS_PROFILE_ACTIONS.ON_GET_NFT_USERS,
      NFTS_PROFILE_ACTIONS.ON_REDEEM_EXCLUSIVE_CONTENT_SUCCESS,
      NFTS_PROFILE_ACTIONS.ON_TRANSFER_NFT_SUCCESS
    ),
    mergeMap(({ payload }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .getNftUsers(
            extractBrandId(state$),
            payload.tokenId,
            accessToken,
            buildNftUsersPaginationFilters(payload, state$)
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => {
              return {
                type: NFTS_PROFILE_ACTIONS.ON_GET_NFT_USERS_SUCCESS,
                payload: parseNftUsersResponse(response.data)
              }
            }),
            catchError(() =>
              of({
                type: NFTS_PROFILE_ACTIONS.ON_GET_NFT_USERS_FAILED
              })
            )
          )
      )
    )
  )

const handleRedeemNftExclusiveContent = (action$, state$) =>
  action$.pipe(
    ofType(NFTS_PROFILE_ACTIONS.ON_REDEEM_EXCLUSIVE_CONTENT),
    mergeMap(({ payload: { tokenId, nftId, userAuthId, claimCount } }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .redeemNftExclusiveContent(
            extractBrandId(state$),
            tokenId,
            buildRedeemExclCntData(userAuthId, nftId, claimCount),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(() => {
              return {
                type: NFTS_PROFILE_ACTIONS.ON_REDEEM_EXCLUSIVE_CONTENT_SUCCESS,
                payload: { tokenId: tokenId }
              }
            }),
            catchError(() =>
              of({
                type: NFTS_PROFILE_ACTIONS.ON_REDEEM_EXCLUSIVE_CONTENT_FAILED
              })
            )
          )
      )
    )
  )

export const nftEpic = combineEpics(
  handleCreateNftEpic,
  handleGetNftsEpic,
  handleOnNftsFilterChangeEpic,
  handleUpdateNftEpic,
  handleGetNftUsers,
  handleRedeemNftExclusiveContent,
  handleTransferNftEpic
)

const extractCreateNftData = (state, mediaUrl, onramp) => {
  const nftsReducer = state.value.nftsReducer
  return nftFieldsFormat(nftsReducer.nftsProfile, mediaUrl, onramp)
}

const buildNftUsersPaginationFilters = (payload, state) => {
  const pagination = extractNftUsersPagination(state)
  return buildPaginationFilters(payload, pagination)
}

const buildTransferNftData = (authId, tokenNftId) => {
  return {
    transaction: {
      type: LOYALTY_EVENT_TYPES.CREDIT_NFT,
      token_id: tokenNftId,
      user_auth_id: authId
    }
  }
}
